import {
  AccountInfo,
  InteractionStatus,
  IPublicClientApplication,
  Logger,
} from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useEffect, useState } from 'react';

// Extend Window interface for E2E test flag
declare global {
  interface Window {
    __E2E_TEST__?: boolean;
  }
}

export interface AuthState {
  instance: IPublicClientApplication;
  account: AccountInfo | null;
  inProgress: InteractionStatus;
  logger: Logger;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isEditor: boolean;
  isViewer: boolean;
  userId: string;
  roles: Roles[];
}

export type Roles = 'admin' | 'editor' | 'viewer' | 'No roles assigned';

function extractRoles(account: AccountInfo | null): Roles[] {
  const userRoles = (account?.idTokenClaims?.roles as string[]) || [];
  const userGroups = (account?.idTokenClaims?.groups as string[]) || [];

  const extractedRoles =
    userRoles.length > 0
      ? userRoles
      : userGroups.map(group => group.toLowerCase());

  return (
    extractedRoles.length > 0 ? extractedRoles : ['No roles assigned']
  ) as Roles[];
}

export default function useAuth(): AuthState {
  const { instance, accounts, inProgress, logger } = useMsal();
  const [account, setActiveAccount] = useState<AccountInfo | null>(null);
  const [roles, setRoles] = useState<Roles[]>([]);
  const [isAuthenticated, setAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEditor, setIsEditor] = useState(false);
  const [isViewer, setIsViewer] = useState(false);
  const [userId, setUserId] = useState('Unknown User');

  // E2E test bypass: Return authenticated state when __E2E_TEST__ flag is set.
  // Gated to non-production builds so the bypass is dead code in prod and cannot
  // be triggered by setting the flag in a production browser session.
  const isE2ETest =
    typeof window !== 'undefined' &&
    window.__E2E_TEST__ === true &&
    process.env.NODE_ENV !== 'production';

  useEffect(() => {
    // In E2E tests, set mock authenticated state
    if (isE2ETest) {
      // Create mock account object to bypass login screen in App.tsx
      const mockAccount: AccountInfo = {
        homeAccountId: 'e2e-test-user.e2e-tenant',
        environment: 'login.windows.net',
        tenantId: 'e2e-tenant',
        username: 'e2e-test@lgmpharma.com',
        localAccountId: 'e2e-test-user',
        name: 'E2E Test User',
        idTokenClaims: {
          roles: ['admin'],
        },
      };

      setActiveAccount(mockAccount);
      setAuthenticated(true);
      setIsAdmin(true);
      setRoles(['admin']);
      setUserId('e2e-test@lgmpharma.com');
      return;
    }

    function getAccount() {
      return (
        instance.getActiveAccount() ||
        (accounts && accounts.length > 0 ? accounts[0] : null)
      );
    }

    const currentAccount = getAccount();
    const currentRoles = extractRoles(currentAccount);

    setActiveAccount(currentAccount);
    setRoles(currentRoles);
    setAuthenticated(currentAccount !== null);
    setIsAdmin(currentRoles.includes('admin'));
    setIsEditor(currentRoles.includes('editor'));
    setIsViewer(currentRoles.includes('viewer'));
    setUserId(
      currentAccount?.username ||
        currentAccount?.name ||
        currentAccount?.idTokenClaims?.preferred_username ||
        'Unknown User'
    );
  }, [instance, accounts, isE2ETest]);

  return {
    instance,
    account,
    inProgress,
    logger,
    isAuthenticated,
    isAdmin,
    isEditor,
    isViewer,
    userId,
    roles,
  };
}
